import { apiClient, apiGet, apiPost } from './client';
import type { ApiResponse } from '@/types';
import type { AuthResponse, LoginRequest, RegisterRequest } from '@/types';
import type { TenantRegistrationRequest, TenantRegistrationResponse } from '@/types';

export const authApi = {
  login(req: LoginRequest) {
    return apiPost<AuthResponse, LoginRequest>('/auth/login', req);
  },

  refresh(refreshToken: string) {
    return apiPost<AuthResponse, { refreshToken: string }>('/auth/refresh', { refreshToken });
  },

  register(req: RegisterRequest) {
    return apiPost<AuthResponse, RegisterRequest>('/auth/register', req);
  },

  changePassword(req: { oldPassword: string; newPassword: string; confirmPassword: string }) {
    return apiPost<void, typeof req>('/auth/change-password', req);
  },

  /**
   * Public availability check — no auth token required.
   * Returns { available: boolean; message: string }
   */
  checkUsername(username: string, tenantType: string) {
    return apiGet<{ available: boolean; message: string }>(
      `/tenants/check-username?username=${encodeURIComponent(username)}&tenantType=${encodeURIComponent(tenantType)}`
    );
  },

  registerTenant(req: TenantRegistrationRequest) {
    return apiPost<TenantRegistrationResponse, TenantRegistrationRequest>(
      '/tenants/register',
      req
    );
  },

  /** Public — no auth required. Returns id + softwareName for registration dropdown.
   *  Pass industry to get industry-filtered results. */
  async listPublicSoftwareVendors(industry?: string) {
    const resp = await apiClient.get<Array<{ id: number; softwareName: string }>>(
      '/admin/software-vendors/public',
      industry ? { params: { industry } } : undefined
    );
    return resp.data;
  },

  /** Fetch the current user's stable API key (survives password changes). */
  getApiKey() {
    return apiGet<{ apiKey: string }>('/auth/my-api-key');
  },

  /**
   * Rotate the API key — the old key is immediately invalid.
   * Returns the new key value.
   */
  regenerateApiKey() {
    return apiPost<{ apiKey: string }, Record<string, never>>('/auth/regenerate-api-key', {});
  },

  /**
   * Upload a Drug License certificate during the registration flow.
   *
   * Called by RegisterTenantPage right after the JSON registration call
   * returns, when the user has selected a file. Authenticated by the
   * one-time {@code uploadToken} returned in the registration response —
   * NOT by JWT, because the user has not logged in yet.
   *
   * The endpoint is multipart, so we bypass apiPost (which forces
   * application/json) and use the raw axios client; axios will set the
   * correct Content-Type with boundary automatically when handed FormData.
   */
  async uploadDlCertificate(
    tenantId: number,
    uploadToken: string,
    file: File,
  ): Promise<void> {
    const form = new FormData();
    form.append('file', file);
    await apiClient.post<ApiResponse<unknown>>(
      `/tenants/${tenantId}/dl-certificate`,
      form,
      {
        params: { token: uploadToken },
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
  },
};
